import CardCommon from '@op/components/CardCommon';
import React, { useEffect, useMemo, useState } from 'react';
import { Bubble } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useLocalLanguage } from '@/pages/opinion/utils/languageValue';
import { useRequestStats } from '@op/pages/feeds/hooks/useFeedsRequest';
import { percentageFormat, reviewsFormat } from '@op/utils/value';
import BubbleCommon from '@op/components/BubbleCommon';
import { keyOpinionsGPTSummary, keyOpinionsGPTSummaryTips } from '@op/components/BubbleCommon/copywritingKey/keyOpinions';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { formatTimeToShow } from '@op/utils/time';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import Gpt from '@op/components/GPT';
import useCustomDefaultPrompt from '@op/pages/AIAnalysisHub/CustomAISummary/hooks/useCustomDefaultPrompt';
import { AiIconMap, DEFAULT_MODAL } from '@op/components/GPT/constants';
import { useGPTReport } from '../../hooks/useGPTReport';
import { GPTType } from '@op/components/GPT/types';

const tabMap: { [key: string]: { tab: string; title: string } } = {
  total: {
    tab: 'socialMedia',
    title: 'Total',
  },
  game_store: {
    tab: 'gameStore',
    title: 'Game Store',
  },
  news: {
    tab: 'newMedia',
    title: 'News',
  },
  social: {
    tab: 'socialMedia',
    title: 'Social Media',
  },
};

const sentimentMap: any = {
  positive: {
    title: 'Positive',
    params: { sentiment: [5] },
  },
  negative: {
    title: 'Negative',
    params: { sentiment: [1] },
  },
  neutral: {
    title: 'Neutral',
    params: { sentiment: [3] },
  },
  total: {
    title: 'Total',
    params: {},
  },
};

export default function GPT({
  languageType,
  filter,
  gameInfo,
  wholeGameInfo,
  isFilterChanged,
  channelOption,
  sentimentOption,
  isSteam,
}: any) {
  const { formatMessage } = useLocalLanguage({ languageType });
  const { feedsStats, feedsStatsLoading, requestState } = useRequestStats({
    defaultLoading: true,
  });
  const { fetchReport } = useGPTReport();
  const { goPage } = useDrilldownParams();
  const [gptType, setGptType] = useState<GPTType>("");

  const { filterParams, title } = useMemo(() => {
    let channelValue: { [key: string]: string } = {};
    if (channelOption === 'game_store') {
      channelValue = { channel_type: 'comments' };
    } else if (channelOption === 'social') {
      channelValue = { channel_type: channelOption };
    }
    const { params: sentimentParams, title } = sentimentMap[sentimentOption];

    const filterParams = {
      ...sentimentParams,
      translated_to: 'origin',
      sentiment_type: sentimentOption,
      ...channelValue,
      ...filter,
    };

    return {
      filterParams,
      title:
        'Key Opinions AI Analysis_' +
        `Channel(${tabMap[channelOption].title})` +
        '_' +
        `Sentiment(${title})`,
    };
  }, [channelOption, filter, sentimentOption]);

  useEffect(() => {
    requestState(filterParams);
  }, [filterParams]);

  const {
    total,
    positivePercent,
    negativePercent,
    neutralPercent,
    positiveTotal,
    neutralTotal,
    negativeTotal,
  } = useMemo(() => {
    const {
      total = 0,
      bad_total = 0,
      neutral_total = 0,
      good_total = 0,
    } = feedsStats || {};

    let positivePercent = 0;
    let negativePercent = 0;
    let neutralPercent = 0;
    if (total) {
      positivePercent = (good_total / total) * 100;
      negativePercent = (bad_total / total) * 100;
      neutralPercent = (neutral_total / total) * 100;
    }

    return {
      total,
      positivePercent,
      negativePercent,
      neutralPercent,
      positiveTotal: good_total,
      neutralTotal: neutral_total,
      negativeTotal: bad_total,
    };
  }, [feedsStats]);

  function chartTipsElement() {
    return (
      <div className="chart-common-formatter">
        <div className="chart-common-formatter-header">
          <div className="chart-common-formatter-header-left">
            {formatTimeToShow(filter)}
          </div>
          <div className="chart-common-formatter-header-right"></div>
        </div>
        <table className="undefined">
          <tbody>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                {formatMessage({ id: 'Mentions' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(total)}
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#69D19C' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Positive' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(positiveTotal)} (
                {percentageFormat(positivePercent)})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#7E92A1' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Neutral' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(neutralTotal)} (
                {percentageFormat(neutralPercent)})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#F17C86' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Negative' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(negativeTotal)} (
                {percentageFormat(negativePercent)})
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  const { promptList: keyOpinionsPromptList } = useCustomDefaultPrompt({
    gameInfo: wholeGameInfo,
  });

  const requestFuncMap = useMemo(() => {
    if (!isFilterChanged) return undefined;

    return {
      defaultRequest: (filter: any) => {
        return fetchReport(
          { channel_type: channelOption, ...filter },
          gameInfo,
          isSteam,
        ).then((data: any) => {
          setGptType(DEFAULT_MODAL);
          return data;
        });
      },
    };
  }, [isFilterChanged, channelOption]);

  const gptIcon = useMemo(() => {
    const icon = AiIconMap[gptType];
    if (!icon) return null;
    return <img src={icon} className="custom-title-icon" />;
  }, [gptType]);

  return (
    <CardCommon className="dbo-opinions-gpt" contentCard>
      <CardCommon contentCard loading={feedsStatsLoading} loadingHeight={249}>
        <div className="mentions-opinions">
          <Bubble
            className="dbo-noborer-wrap"
            placement="right"
            closeOnScroll
            content={chartTipsElement()}
            dark
          >
            <div className="mentions-opinions-hd">
              <div className="mentions-opinions-title">
                {`${formatMessage({ id: 'Mentions' })}: ${reviewsFormat(
                  total,
                )}`}
              </div>

              <div className="mentions-opinions-bar">
                <div
                  className="mentions-opinion-bar-item"
                  style={{
                    width: `${percentageFormat(positivePercent)}`,
                    backgroundColor: '#69D19C',
                  }}
                ></div>
                <div
                  className="mentions-opinion-bar-item"
                  style={{
                    width: `${percentageFormat(neutralPercent)}`,
                    backgroundColor: '#6E7794',
                  }}
                ></div>
                <div
                  className="mentions-opinion-bar-item"
                  style={{
                    width: `${percentageFormat(negativePercent)}`,
                    backgroundColor: '#F17C86',
                  }}
                ></div>
              </div>
            </div>
          </Bubble>
          <div className="mentions-opinions-bd">
            <span
              className="mentions-go-link"
              onClick={() => {
                buriedBtnPoint('KOOS_GotoFeeds');
                // Feeds 为特殊页面，走本地缓存（需显式传 Local）
                goPage({
                  path: `/opinion/Feeds/Feeds?tab=${tabMap[channelOption].tab}`,
                  filter: {
                    ...filter,
                    ...gameInfo,
                  },
                  mode: EDrilldownMode.Local,
                });
              }}
            >
              {formatMessage({ id: 'Go to Feeds' })}
              <SvgIcon type="arrow-right" />
            </span>
          </div>
        </div>
        {/* gpt生成前 */}
        <Gpt.PageCard
          filter={filterParams}
          hasStopBtn={true}
          requestFuncMap={requestFuncMap}
          autoRequest={false}
          contentCard
          promptList={keyOpinionsPromptList}
          oriFeedsState={feedsStats}
          linkFilterChange={false}
          showModelType={false}
          saveName={title}
          className={'gpt-summary'}
          loadingHeight={160}
          title={
            <div className="custom-title">
              {gptIcon}
              <span>{formatMessage({ id: 'AI Summary' })}</span>
              <BubbleCommon
                showIcon
                tipsMap={keyOpinionsGPTSummary}
              ></BubbleCommon>
            </div>
          }
          promptConfirm={(data: any) => {
            setGptType(data?.modelType || '');
          }}
        >
          {(toggleVisible: any, { total }: { total: number }) => (
            <div className="gpt-summary-entry-tips">
              <Gpt.Button
                disabled={total <= 0}
                onClick={() => toggleVisible()}
                tipsMap={total <= 0 ? keyOpinionsGPTSummaryTips : undefined}
              ></Gpt.Button>
            </div>
          )}
        </Gpt.PageCard>
      </CardCommon>
    </CardCommon>
  );
}
